/**
 * SourceCropModal — draw a region on a page of a question's source paper.
 *
 * Shows the page as it was read (upright, masks tinted, the import crop
 * outlined) and hands back the page number and a rect in FRACTIONS of that
 * page. It cuts nothing itself: the server re-renders the page at full
 * resolution and crops there, so the picture a student sees is never a crop of
 * this preview.
 *
 * See docs/FeatureDesigns/question-image-recrop-target.md.
 */
import { useState } from 'react';
import ReactCrop, { type PercentCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import type { PageRect } from '@padcanvas/shared';
import './ImageCropModal.css';

export interface SourceCropPage {
  page: number;
  url: string;
  /** False: the page cannot be cut from, and `unavailableNote` says why. */
  croppable: boolean;
}

export interface SourceCropModalProps {
  title?: string;
  pages: SourceCropPage[];
  initialPage?: number;
  /** The region on record for this image, drawn when its page is shown. */
  initialRect?: PageRect;
  unavailableNote?: string;
  onApply: (page: number, rect: PageRect) => Promise<void>;
  onClose: () => void;
}

const toCrop = (r: PageRect): PercentCrop =>
  ({ unit: '%', x: r.x * 100, y: r.y * 100, width: r.w * 100, height: r.h * 100 });

export function SourceCropModal({
  title = 'Crop from paper',
  pages,
  initialPage,
  initialRect,
  unavailableNote = 'This page was edited at import and those edits are not on record, so it cannot be cropped yet.',
  onApply,
  onClose,
}: SourceCropModalProps) {
  const startPage = pages.some(p => p.page === initialPage) ? initialPage! : pages[0]?.page;
  const [page, setPage] = useState<number | undefined>(startPage);
  const [crop, setCrop] = useState<PercentCrop | undefined>(
    initialRect && startPage === initialPage ? toCrop(initialRect) : undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const current = pages.find(p => p.page === page);
  const hasRegion = !!crop && crop.width > 0 && crop.height > 0;

  const choosePage = (next: number) => {
    if (next === page) return;
    setPage(next);
    // A rect means nothing on another page, except the one it was recorded on.
    setCrop(initialRect && next === initialPage ? toCrop(initialRect) : undefined);
    setError('');
  };

  const apply = async () => {
    if (!current || !crop || !hasRegion) return;
    setBusy(true);
    setError('');
    try {
      await onApply(current.page, { x: crop.x / 100, y: crop.y / 100, w: crop.width / 100, h: crop.height / 100 });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Crop failed');
      setBusy(false);
    }
  };

  return (
    <div className="sui-image-crop-overlay" onClick={onClose}>
      <div className="sui-image-crop-modal sui-source-crop-modal" onClick={e => e.stopPropagation()}>
        <div className="sui-image-crop-header">
          <h2>{title}</h2>
          <button className="sui-image-crop-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        {pages.length > 1 && (
          <div className="sui-source-crop-pages" role="tablist" aria-label="Pages">
            {pages.map(p => (
              <button
                key={p.page}
                type="button"
                role="tab"
                aria-selected={p.page === page}
                className={`sui-source-crop-page${p.page === page ? ' active' : ''}`}
                onClick={() => choosePage(p.page)}
              >
                p. {p.page}
              </button>
            ))}
          </div>
        )}
        <div className="sui-image-crop-body">
          {current && (
            <ReactCrop
              crop={crop}
              onChange={(_, percent) => setCrop(percent)}
              disabled={!current.croppable || busy}
            >
              <img
                key={current.url}
                src={current.url}
                alt={`Page ${current.page}`}
                style={{ maxWidth: '100%', maxHeight: '65vh' }}
              />
            </ReactCrop>
          )}
        </div>
        {current && !current.croppable && <span className="sui-image-crop-error">{unavailableNote}</span>}
        {error && <span className="sui-image-crop-error">{error}</span>}
        <div className="sui-image-crop-footer">
          <span className="sui-source-crop-hint">Drag over the figure. The image is cut from the full-resolution page.</span>
          <button type="button" className="sui-image-crop-btn-cancel" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="sui-image-crop-btn-apply"
            onClick={apply}
            disabled={busy || !hasRegion || !current?.croppable}
          >
            {busy ? 'Cropping...' : 'Use this region'}
          </button>
        </div>
      </div>
    </div>
  );
}
